import Inplace from '@/components/landing/common/Inplace';
import CodeWindow from '@/components/landing/developers/CodeWindow';

// ─── Data ─────────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    badge: 'WORKFLOW',
    heading: 'Fetch, Frame,\nGraph',
    body: 'Pull historical market data through the Python financial data API, transform it into a dataframe, and generate visualizations with a few lines of code. Spend less time wiring tools together and more time analyzing markets.',
    cta: 'Get Started Free',
    ctaUrl: '/login',
    visual: 'workflow',
  },
  {
    badge: 'ANALYZE',
    heading: 'Technical \nanalysis \nbuilt in',
    body: 'Financials, earnings, disclosures, and macro data structured directly from source material. Trace every number back to reality with no black boxes in between, a financial analytics API you can trust.',
    cta: 'Explore Datasets',
    ctaUrl: '/products',
    visual: 'analysis',
  },
  {
    badge: 'IDIOMATIC',
    heading: 'All the data,\nwithout the scraping',
    body: 'Access profiles, market statistics, earnings calendars, sectors, industries, and key company metadata through a clean, predictable market data SDK interface designed for developers.',
    cta: 'See All Requests',
    ctaUrl: '/docs',
    visual: 'cleancode',
  },
  {
    badge: 'MODELING',
    heading: 'Forecast\nfaster',
    body: 'Work with economic datasets, transform raw observations into signals, and train forecasting models directly from structured data pipelines built into the quantitative trading API.',
    cta: 'Start Training',
    ctaUrl: '/login',
    visual: 'modeling',
  },
  {
    badge: 'SCALE',
    heading: 'ETL Native',
    body: 'Parallel utilities in the Python finance library help you fetch filings, fundamentals, and market data across large watchlists with minimal code, making large-scale research workflows simple.',
    cta: 'Start Building',
    ctaUrl: '/login',
    visual: 'scale',
  },

];

const V1 = () => (
  <CodeWindow
    filename={'price_candles.py'}
    bg={'/24.jpg'}
    code={`from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

df = utils.df(
    client.stocks.prices(
        "NVDA",
        from_date=utils.d(utils.yearago),
        frame="daily",
    )
)

visualize.candles(df)
`}
  />
);

const V2 = () => (
  <CodeWindow
    filename={'spot_direction.py'}
    bg={'/4.jpeg'}
    code={`from axion import Axion, utils, visualize, ta

client = Axion(api_key="your_api_key")

df = utils.df(
    client.forex.prices(
        "EURUSD",
        from_date=utils.d(utils.yearago),
        frame="daily",
    )
)

df["ema_21"] = ta.ema(df, period=21)
df["ema_50"] = ta.ema(df, period=50)
df["rsi"]    = ta.rsi(df, period=14)

visualize.graph(
    df,
    x="date",
    lines=["close", "ema_21", "ema_50"],
    title="EUR/USD - Spot Direction",
)
`}
  />
);

const V3 = () => (
  <CodeWindow
    filename={'company_profile.py'}
    bg={'/10.png'}
    code={`from axion import Axion

client = Axion(api_key="your_api_key")
ticker = "NVDA"

profile  = client.profiles.profile(ticker)
info     = client.profiles.info(ticker)
summary  = client.profiles.summary(ticker)
calendar = client.profiles.calendar(ticker)

sector   = profile["sector"]
industry = profile["industry"]
price    = summary["regularMarketPrice"]
high_52w = summary["fiftyTwoWeekHigh"]
earnings = calendar["earnings"]["earningsDate"]
`}
  />
);

const V4 = () => (
  <CodeWindow
    filename={'cpi_inflation.py'}
    bg={'/13.png'}
    code={`from axion import Axion, utils, models, visualize

client = Axion(api_key="your_api_key")

cpi_id = client.econ.search("CPI all items")[0]["id"]
cpi = utils.df(client.econ.dataset(cpi_id))

# Convert price levels to a monthly inflation rate
cpi = utils.relativity(cpi, cols=["value"])

# Project inflation six months forward
forecast = models.linearRegression(
    cpi,
    x="date",
    target="relative_value",
    n_preds=6,
    scale="M"
)

visualize.line(cpi, x="date", y="relative_value")`}
  />
);

const V5 = () => (
  <CodeWindow
    filename={'sec_filings_monitor.py'}
    bg={'/8.jpeg'}
    code={`from axion import Axion, utils

client = Axion(api_key="your_api_key")

watchlist = ["AAPL", "MSFT", "GOOGL", "TSLA", "META"]

def fetch_filings(row):
    t = row["ticker"]
    return (t, {
        "ticker":    t,
        "last_10k":  client.filings.recent(t, form="10-K", limit=1)[0]["date"],
        "last_10q":  client.filings.recent(t, form="10-Q", limit=1)[0]["date"],
        "recent_8k": client.filings.recent(t, form="8-K", limit=1)[0]["title"],
    })

filings_map = {}
utils.work(utils.df([{"ticker": t} for t in watchlist]), fetch_filings, filings_map)

df_filings = utils.df(list(filings_map.values()))
`}
  />
);

const VISUALS = {
  workflow:   V1,
  analysis: V2,
  cleancode:    V3,
  modeling:     V4,
  scale:     V5,
};


const CodeInplace = () => {
  return <Inplace
    full={true}
    visuals={VISUALS}
    slides={FEATURES}
  />
};

export default CodeInplace;
